import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { useQueryParam } from '@plato/ui-legacy/lib/hooks/use-query-param';
import { stringParam } from '@plato/ui-legacy/lib/query-params';
import { Activity } from 'lucide-react';
import { useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityFeedScope } from '../../activity-feed/components/global-activity-feed.tsx';
import type { ActivityFeedLinkSource } from '../../activity-feed/hooks/use-activity-filters.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useStickyBarRef } from '../hooks/use-sticky-bar-ref.ts';
import { ActivityFilterBar } from './activity-filter-bar.tsx';
import { GlobalActivityFeed } from './global-activity-feed.tsx';

export type NewGlobalActivityFeedProps = {
    scope: ActivityFeedScope;
};

/**
 * The reworked /activities page, behind `enableNewCustomerDetailView`: the new
 * filter bar over the section-card feed. OWN pins it to the signed-in user.
 */
export function NewGlobalActivityFeed({ scope }: NewGlobalActivityFeedProps): ReactElement {
    const { t } = useTranslation();
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();
    const isOwnScope = scope === ActivityFeedScope.OWN;
    const stickyBarRef = useStickyBarRef();

    const [source] = useQueryParam('source', stringParam<ActivityFeedLinkSource>(), 'direct');

    useEffect(() => {
        sendEvent('global_activity_feed_viewed', { source });
    }, [sendEvent, source]);

    return (
        // A white header band over a grey page, like the customer page, so the feed's cards rest on grey.
        <Page className="gap-0 bg-neutral-50 px-0! pb-0">
            <div className="bg-white px-5 pb-3 sm:px-4">
                <PageHeader
                    icon={Activity}
                    title={isOwnScope ? t('page_title__my_activity') : t('page_title__activities')}
                    subTitle={isOwnScope ? t('page_sub_title__my_activity') : t('page_sub_title__activities')}
                />
            </div>
            {/* The gutter sits outside the width cap, so it does not narrow the feed. */}
            <div className="flex flex-1 flex-col px-gutter py-page">
                <section className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-section [--section-card-sticky-bg:var(--color-neutral-50)] [--section-card-sticky-gap:0px]">
                    {/* Sticky over the feed like the old page's filter row, on the page's grey. */}
                    <ActivityFilterBar
                        ref={stickyBarRef}
                        scope="global"
                        showUserFilter={!isOwnScope}
                        className="sticky top-0 z-10 -my-cozy bg-neutral-50 py-cozy"
                    />
                    <GlobalActivityFeed userIds={isOwnScope ? [userId] : undefined} />
                </section>
            </div>
        </Page>
    );
}
